import React, { createContext, useContext } from 'react'
import PropTypes from 'prop-types'

/*
 * I created this context in a way that it could be reused in the likely event that we'll run into
 * issues with prop-drilling. Because of the way contexts work you can nest the same provider within
 * itself so that components that are further down the component tree can still share values across
 * components with out requiring parent elements to rerender. A consuming component (one that uses
 * useContext()) will only retrieve values from the most recent parent provider. So if you have data
 * in a higher level provider that you wish to pass to a nested provider make sure that you pass
 * the desired value to the nested CommonUseProvider. Here's an example (they are numbered to help
 * with comprehension):
 *
 * <CommonUseProvider>
 *   <ConsumingComponent_1>
 *     <CommonUseProvider_2>
 *       <ConsumingComponent_2 />
 *     </CommonUseProvider_2>
 *   </ConsumingComponent_1>
 * </CommonUseProvider>
 *
 * ConsumingComponent_2 will only be able to access values provided by its most recent parent
 * provider. In this case, CommonUseProvider_2. If there are some, but not all, values that
 * you wish to use with ConsumingComponent_2 you must pass them as part of the defaultValues when
 * you create the CommonUseProvider_2 within ConsumingComponent_1.
 *
 * If components subscribe to all of the same values just use a single context.
 *
 * As with contexts in general, only create a nested context if there are multiple children that
 * will require access to common values and passing the values normally as props goes down multiple
 * levels and will lead to too much complexity (prop-drilling).
 */

export const CommonUseContext = createContext()

/**
 * @param {Object} defaultValues The values you wish to make available to children of the provider
 */
export function CommonUseProvider({ children, defaultValues = {} }) {
  return <CommonUseContext.Provider value={defaultValues}>{children}</CommonUseContext.Provider>
}

CommonUseProvider.propTypes = {
  children: PropTypes.node,
  defaultValues: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.array,
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.func,
      PropTypes.symbol,
      PropTypes.node,
      PropTypes.element,
    ])
  ),
}

export function useCommon() {
  return useContext(CommonUseContext)
}
